import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { ContractAppendSections } from "./ContractAppendSections";
import { resolveCompanyLogoUrl } from "../utils/persistableBlobUrl";

/**
 * Commercial / legal terms that differ per engagement (credit period, invoice-objection window,
 * termination notice, jurisdiction) are NOT hard-coded: they come from the contract draft fields
 * below, set by the Lead Advisor. While a term is unset its sentence is omitted rather than
 * filled with a default that no one agreed to.
 */
export const CONTRACT_TERM_FIELDS = Object.freeze([
  { key: "creditPeriodDays", label: "Credit period (calendar days)", kind: "days" },
  { key: "invoiceObjectionDays", label: "Invoice objection window (days)", kind: "days" },
  { key: "terminationNoticeDays", label: "Termination notice (days)", kind: "days" },
  { key: "jurisdiction", label: "Jurisdiction (courts at)", kind: "text" },
]);

const TERM_CLAUSE_INDEX = { creditPeriodDays: 1, invoiceObjectionDays: 3, terminationNoticeDays: 13, jurisdiction: 19 };
const termDays = (sections, key) => {
  const n = Number(String(sections?.[key] ?? "").trim());
  return Number.isFinite(n) && n > 0 ? n : null;
};
const termText = (sections, key) => String(sections?.[key] ?? "").trim();

const buildClauses = (sections) => [
  { title: "Clause 1. SERVICES", body: 'A) Service provider shall perform the professional services ("Services") as described in the exhibits attached hereto.\nB) Service provider shall at all times comply with all applicable laws, regulations, rules, relevant to its provision of the Services.\nC) Client shall ensure that Service provider has necessary resources for effective discharge of services.' },
  { title: "Clause 2. PAYMENT TERMS", body: "A. Taxes: Any service tax or local levy applicable at the client's site shall be borne by the Client.\nB. Exclusion: This proposed cost excludes any cost due towards the certifying body (Third Party). Out of pocket expenses borne by Service Provider shall be reimbursed.\nC. Payment terms: Invoices shall be raised as per the payment schedule in Exhibit A." + (termDays(sections, "creditPeriodDays") ? ` Credit period: ${termDays(sections, "creditPeriodDays")} calendar days.` : "") + "\nD. Project performance depends on: Client commitment, resource availability, and strict adherence to the project plan." },
  { title: "Clause 3. DURATION OF AGREEMENT", body: "" },
  { title: "Clause 4. INVOICES AND PAYMENTS", body: "Service provider shall invoice Client for fees as per Clause 2 above." + (termDays(sections, "invoiceObjectionDays") ? ` If Client objects to all or any portion of any invoice, Client shall notify within ${termDays(sections, "invoiceObjectionDays")} days from receipt.` : "") },
  { title: "Clause 5. INDEMNIFICATION", body: "Both parties agree to indemnify, defend, and hold harmless against claims, damages, and liabilities arising out of breach of this Agreement or gross negligence." },
  { title: "Clause 6. PROPRIETARY INFORMATION", body: "All trade secrets or confidential information designated as such in writing by the Disclosing Party shall be protected." },
  { title: "Clause 7. CONFIDENTIALITY", body: "Each party shall keep secret and treat in strictest confidence all confidential information received about the other party." },
  { title: "Clause 8. NON-DISCLOSURE", body: "The Recipient will use Proprietary Information only for the purpose for which it was disclosed and protect it against third-party disclosure." },
  { title: "Clause 9. RETURN OF DOCUMENTS", body: "Upon written request, the Recipient shall return all drawings, documents, and tangible manifestations of Proprietary Information." },
  { title: "Clause 10. COMMUNICATIONS", body: "Written communications requesting or transferring Proprietary Information shall be addressed to the respective designees." },
  { title: "Clause 11. USE OF WORK PRODUCT", body: "Deliverables shall be used solely for internal purposes of Client. Service provider shall not be referred to in any public documents without prior written consent." },
  { title: "Clause 12. LIMITATION OF LIABILITY", body: "Neither party shall be liable for indirect, consequential, incidental, or special losses. Claims shall be limited to actual, direct damages." },
  { title: "Clause 13. LIMIT ON OBLIGATIONS", body: "Obligations shall not apply where Proprietary Information is publicly known, independently developed, or required to be disclosed by law." },
  { title: "Clause 14. TERMINATION", body: "This Agreement shall remain in force until completion of Services, end of duration (Clause 3)" + (termDays(sections, "terminationNoticeDays") ? `, or termination by either party with ${termDays(sections, "terminationNoticeDays")} days written notice` : "") + "." },
  { title: "Clause 15. WAIVER", body: "A waiver of any term shall not constitute a precedent or bind either Party to a waiver of any succeeding breach." },
  { title: "Clause 16. ENTIRE AGREEMENT", body: "This Agreement, including Exhibits and addenda, constitutes the entire Agreement and supersedes all prior communications." },
  { title: "Clause 17. ASSIGNMENT", body: "This Agreement shall be binding upon successors. It shall not be assigned without first obtaining the written consent of the other party." },
  { title: "Clause 18. SEVERABILITY", body: "If any provision is held void or unenforceable, the remaining provisions shall remain in full force and effect." },
  { title: "Clause 19. NOTICES", body: "Any information or notices required in writing shall be deemed sufficiently given if delivered personally or by certified mail." },
  { title: "Clause 20. DISPUTE RESOLUTION", body: "Both parties agree to amicably resolve disputes." + (termText(sections, "jurisdiction") ? ` Jurisdiction: Courts at ${termText(sections, "jurisdiction")}.` : "") },
  { title: "Clause 21. GOVERNING LAW", body: "This Agreement shall be governed and interpreted pursuant to the laws of India." },
];

/**
 * Optional DOM anchors for contract review comments (`data-contract-anchor`).
 */
function ax(annotateAnchors, anchorId, extraProps) {
  if (!annotateAnchors || !anchorId) return extraProps || {};
  return { ...extraProps, "data-contract-anchor": anchorId };
}

/**
 * Scrollable MSA–NDA + Exhibit A. Exhibit RACI / resource tables optionally edit the same row state as the scoping tabs.
 */
export function MsaContractBody({
  isContractEditMode,
  contractSections,
  setContractSections,
  raciRows,
  updateRaciCell,
  resourceTable,
  updateResourceCell,
  project,
  contractExhibitEditable,
  annotateAnchors = false,
  appendSections = [],
  onAppendSectionsChange,
  appendSectionsEditable = false,
  appendSectionNumberOffset = 1,
  scrollClassName = "paper-surface overflow-y-auto p-10 space-y-8 flex-1",
  scrollStyle = { fontFamily: "'Times New Roman', Georgia, serif", lineHeight: "1.7" },
  clientFacing = false,
  /** Advisory firm logo (AdvisoryFirm.logoBlobUrl); nothing is rendered when it is not known. */
  advisorLogoUrl = "",
}) {
  const raciColIdx = [1, 2, 3, 4, 5, 6];
  /**
   * §4 / §B.8 (.claude/design/integrations-module.md) contract-render trim: the client-facing
   * render (e-sign send / manual-upload reference) must fail closed rather than silently cite
   * the "PRJ-2025-001" dev-placeholder on an executed legal document. The LA/SPOC internal
   * editing view (clientFacing=false, the default — both current call sites in
   * ProjectWorkspace.jsx) is unaffected and keeps today's fallback behaviour exactly as-is.
   */
  if (clientFacing && !project?.id) {
    throw new Error(
      "MsaContractBody: refusing to render the client-facing contract without a real project.id (would otherwise fall back to a placeholder id on a legal document)."
    );
  }
  /** Commandment VIII: client company branding, sourced from project.profile (already fetched — no new call). */
  const companyLogoUrl = resolveCompanyLogoUrl(project);
  const advisorLogo = advisorLogoUrl || contractSections.advisorLogoUrl || "";
  return /* @__PURE__ */ jsxs("div", {
    className: scrollClassName,
    style: scrollStyle,
    ...ax(annotateAnchors, "document.root"),
    children: [
      /* @__PURE__ */ jsxs("div", {
        className: "text-center border-b-2 border-slate-200 pb-6 space-y-1",
        ...ax(annotateAnchors, "section.docHeader"),
        children: [
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between",
            children: [
              companyLogoUrl
                ? /* @__PURE__ */ jsx("img", {
                    src: companyLogoUrl,
                    alt: "Company Logo",
                    className: "h-8 w-auto max-w-[160px] object-contain",
                  })
                : /* @__PURE__ */ jsx("span", {}),
              advisorLogo
                ? /* @__PURE__ */ jsx("img", {
                    src: advisorLogo,
                    alt: "Advisor Logo",
                    className: "h-8 w-auto max-w-[160px] object-contain",
                  })
                : /* @__PURE__ */ jsx("span", {}),
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between text-sm font-semibold text-slate-600 mt-2",
            children: [
              isContractEditMode
                ? /* @__PURE__ */ jsx("input", {
                    value: contractSections.companyName,
                    onChange: (e) => setContractSections((p) => ({ ...p, companyName: e.target.value })),
                    className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 flex-1 mr-4",
                  })
                : /* @__PURE__ */ jsx("span", { children: contractSections.companyName }),
              isContractEditMode
                ? /* @__PURE__ */ jsx("input", {
                    value: contractSections.advisorName,
                    onChange: (e) => setContractSections((p) => ({ ...p, advisorName: e.target.value })),
                    className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 flex-1 ml-4",
                  })
                : /* @__PURE__ */ jsx("span", { children: contractSections.advisorName }),
            ],
          }),
          /* @__PURE__ */ jsx("p", { className: "text-rose-600 text-xs font-semibold mt-3 tracking-widest uppercase", children: "Confidential" }),
          /* @__PURE__ */ jsx("h2", { className: "text-xl font-black mt-4 tracking-wide text-slate-900", children: "MASTER SERVICES - CONFIDENTIALITY & NON-DISCLOSURE AGREEMENT" }),
          /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 font-bold", children: "(MSA \u2013 NDA)" }),
        ],
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "space-y-3 text-sm text-slate-700",
        ...ax(annotateAnchors, "section.introBody"),
        children: [
          /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx("strong", { className: "text-slate-800", children: "Main Section" }) }),
          /* @__PURE__ */ jsxs("p", {
            children: [
              'This Agreement for rendering Services ("Agreement") is made and executed on ',
              /* @__PURE__ */ jsx("strong", {
                children: isContractEditMode
                  ? /* @__PURE__ */ jsx("input", {
                      value: contractSections.effectiveDate,
                      onChange: (e) => setContractSections((p) => ({ ...p, effectiveDate: e.target.value })),
                      className: "bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-300 inline w-48",
                    })
                  : contractSections.effectiveDate,
              }),
              ' (Hereafter "effective date") between:',
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "bg-white rounded-xl p-4 border border-slate-200 my-3 space-y-1",
            children: [
              isContractEditMode
                ? /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsx("input", {
                        value: contractSections.advisorName,
                        onChange: (e) => setContractSections((p) => ({ ...p, advisorName: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-bold text-slate-800 w-full outline-none focus:ring-2 focus:ring-blue-300 mb-1",
                      }),
                      /* @__PURE__ */ jsxs("div", {
                        className: "flex gap-2",
                        children: [
                          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 shrink-0", children: "PAN:" }),
                          /* @__PURE__ */ jsx("input", {
                            value: contractSections.advisorPAN,
                            onChange: (e) => setContractSections((p) => ({ ...p, advisorPAN: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-xs w-40 outline-none focus:ring-2 focus:ring-blue-300",
                          }),
                          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 shrink-0", children: "GSTN:" }),
                          /* @__PURE__ */ jsx("input", {
                            value: contractSections.advisorGST,
                            onChange: (e) => setContractSections((p) => ({ ...p, advisorGST: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-xs w-52 outline-none focus:ring-2 focus:ring-blue-300",
                          }),
                        ],
                      }),
                    ],
                  })
                : /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsx("p", { className: "font-bold text-slate-800", children: contractSections.advisorName }),
                      /* @__PURE__ */ jsxs("p", {
                        className: "text-xs text-slate-500",
                        children: ["PAN: ", contractSections.advisorPAN, " / GSTN: ", contractSections.advisorGST],
                      }),
                    ],
                  }),
              /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 italic", children: '(Hereafter "Service Provider")' }),
            ],
          }),
          /* @__PURE__ */ jsx("p", { className: "text-center font-bold text-slate-500", children: "and" }),
          /* @__PURE__ */ jsxs("div", {
            className: "bg-white rounded-xl p-4 border border-slate-200 my-3 space-y-1",
            children: [
              isContractEditMode
                ? /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsx("input", {
                        value: contractSections.companyName,
                        onChange: (e) => setContractSections((p) => ({ ...p, companyName: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-bold text-slate-800 w-full outline-none focus:ring-2 focus:ring-blue-300 mb-1",
                      }),
                      /* @__PURE__ */ jsxs("div", {
                        className: "flex gap-2",
                        children: [
                          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 shrink-0", children: "PAN:" }),
                          /* @__PURE__ */ jsx("input", {
                            value: contractSections.companyPAN,
                            onChange: (e) => setContractSections((p) => ({ ...p, companyPAN: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-xs w-40 outline-none focus:ring-2 focus:ring-blue-300",
                          }),
                          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 shrink-0", children: "GSTN:" }),
                          /* @__PURE__ */ jsx("input", {
                            value: contractSections.companyGST,
                            onChange: (e) => setContractSections((p) => ({ ...p, companyGST: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-xs w-52 outline-none focus:ring-2 focus:ring-blue-300",
                          }),
                        ],
                      }),
                    ],
                  })
                : /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsx("p", { className: "font-bold text-slate-800", children: contractSections.companyName }),
                      /* @__PURE__ */ jsxs("p", {
                        className: "text-xs text-slate-500",
                        children: ["PAN: ", contractSections.companyPAN, " / GSTN: ", contractSections.companyGST],
                      }),
                    ],
                  }),
              /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 italic", children: '(Hereafter "Client")' }),
            ],
          }),
          /* @__PURE__ */ jsx("p", { children: "In consideration of the mutual promises set forth herein, Client and Service Provider agree as follows:" }),
        ],
      }),
      buildClauses(contractSections).map((clause, ci) => {
        const body =
          ci === 2
            ? `This Agreement is valid for a period of ${isContractEditMode ? "" : contractSections.duration} from the effective date.${isContractEditMode ? "" : " The duration can be further extended upon mutual agreement."}`
            : clause.body;
        return /* @__PURE__ */ jsxs(
          "div",
          {
            className: "my-4",
            ...ax(annotateAnchors, `section.clause.${ci}`),
            children: [
              /* @__PURE__ */ jsx("p", { className: "font-black text-sm text-slate-800 uppercase tracking-wide", children: clause.title }),
              /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-600 mt-1 whitespace-pre-line", children: body }),
              isContractEditMode &&
                CONTRACT_TERM_FIELDS.filter((f) => TERM_CLAUSE_INDEX[f.key] === ci).map((f) =>
                  /* @__PURE__ */ jsxs("label", {
                    className: "flex items-center gap-2 mt-1 text-xs font-semibold text-slate-600",
                    children: [
                      f.label,
                      /* @__PURE__ */ jsx("input", {
                        value: contractSections[f.key] ?? "",
                        inputMode: f.kind === "days" ? "numeric" : void 0,
                        onChange: (e) => setContractSections((p) => ({ ...p, [f.key]: e.target.value })),
                        "aria-label": f.label,
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 w-48",
                      }),
                    ],
                  }, f.key)
                ),
              ci === 2 &&
                isContractEditMode &&
                /* @__PURE__ */ jsx("input", {
                  value: contractSections.duration,
                  onChange: (e) => setContractSections((p) => ({ ...p, duration: e.target.value })),
                  className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-300 mt-1 w-48",
                }),
            ],
          },
          ci
        );
      }),
      /**
       * §B.8: section.witness duplicates section.finalSignatures (identical empty signature
       * blocks for the same two parties) at a stale mid-document position, pre-dating Exhibit A /
       * append-sections. Stripped from the client-facing render; kept as-is for the internal
       * LA/SPOC editing view (clientFacing=false).
       */
      !clientFacing &&
        /* @__PURE__ */ jsxs("div", {
          className: "border-t-2 border-slate-200 pt-4 mt-6",
          ...ax(annotateAnchors, "section.witness"),
          children: [
            /* @__PURE__ */ jsx("p", {
              className: "text-sm text-slate-500 italic",
              children: "IN WITNESS WHEREOF, Client and Service Provider have caused this Agreement to be executed by their duly authorised representatives:",
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "grid grid-cols-2 gap-10 mt-4",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "space-y-2",
                  children: [
                    /* @__PURE__ */ jsxs("p", { className: "text-xs font-bold text-slate-600", children: ["For ", contractSections.companyName] }),
                    /* @__PURE__ */ jsx("div", {
                      className: "h-20 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-xs text-slate-400",
                      "data-testid": "signature-line",
                    }),
                  ],
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "space-y-2",
                  children: [
                    /* @__PURE__ */ jsxs("p", { className: "text-xs font-bold text-slate-600", children: ["For ", contractSections.advisorName] }),
                    /* @__PURE__ */ jsx("div", {
                      className: "h-20 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-xs text-slate-400",
                      "data-testid": "signature-line",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      /* @__PURE__ */ jsxs("div", {
        className: "border-t-4 border-blue-600 pt-8 mt-10 space-y-6",
        ...ax(annotateAnchors, "section.exhibit"),
        children: [
          /* @__PURE__ */ jsxs("div", {
            className: "text-center",
            ...ax(annotateAnchors, "section.exhibitHeader"),
            children: [
              /* @__PURE__ */ jsx("h3", { className: "text-lg font-black tracking-widest uppercase text-blue-700", children: "Exhibit A" }),
              /* @__PURE__ */ jsxs("p", {
                className: "text-sm font-bold text-slate-600 mt-1",
                children: [
                  "SCOPE OF SERVICE FOR PROJECT BEARING ID ",
                  /* @__PURE__ */ jsx("span", { className: "text-blue-600", children: project.id || "PRJ-2025-001" }),
                ],
              }),
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "grid grid-cols-[180px_1fr] gap-y-4 gap-x-4 text-sm",
            ...ax(annotateAnchors, "section.exhibitGrid"),
            children: [
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Effective Date" }),
              /* @__PURE__ */ jsxs("div", {
                ...ax(annotateAnchors, "exhibit.effectiveDate"),
                children: [
                  isContractEditMode
                    ? /* @__PURE__ */ jsx("input", {
                        value: contractSections.effectiveDate,
                        onChange: (e) => setContractSections((p) => ({ ...p, effectiveDate: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-300",
                      })
                    : /* @__PURE__ */ jsx("span", { className: "text-slate-700", children: contractSections.effectiveDate }),
                ],
              }),
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Scope of Work" }),
              /* @__PURE__ */ jsxs("div", {
                ...ax(annotateAnchors, "exhibit.scopeOfWork"),
                children: [
                  isContractEditMode
                    ? /* @__PURE__ */ jsx("textarea", {
                        value: contractSections.scopeOfWork,
                        onChange: (e) => setContractSections((p) => ({ ...p, scopeOfWork: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-300 min-h-[80px]",
                        rows: 4,
                      })
                    : /* @__PURE__ */ jsx("span", { className: "text-slate-700 whitespace-pre-line", children: contractSections.scopeOfWork }),
                ],
              }),
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Project Plan" }),
              /* @__PURE__ */ jsxs("div", {
                ...ax(annotateAnchors, "exhibit.projectPlan"),
                children: [
                  isContractEditMode
                    ? /* @__PURE__ */ jsx("textarea", {
                        value: contractSections.projectPlan,
                        onChange: (e) => setContractSections((p) => ({ ...p, projectPlan: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-300 min-h-[80px]",
                        rows: 4,
                      })
                    : /* @__PURE__ */ jsx("span", { className: "text-slate-700 whitespace-pre-line", children: contractSections.projectPlan }),
                ],
              }),
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs col-span-2", children: "RACI" }),
              /* @__PURE__ */ jsx("div", {
                className: "col-span-2",
                children: /* @__PURE__ */ jsx("div", {
                  className: "overflow-x-auto rounded-xl border border-slate-200",
                  children: /* @__PURE__ */ jsxs("table", {
                    className: "w-full text-left text-xs",
                    children: [
                      /* @__PURE__ */ jsx("thead", {
                        children: /* @__PURE__ */ jsxs("tr", {
                          className: "bg-slate-900 text-[9px] font-black uppercase tracking-widest text-blue-300",
                          children: [
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Phase" }),
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Task" }),
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Responsible" }),
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Accountable" }),
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Consulted" }),
                            /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: "Informed" }),
                          ],
                        }),
                      }),
                      /* @__PURE__ */ jsx("tbody", {
                        className: "divide-y divide-slate-100",
                        children: raciRows.map((row, ri) =>
                          /* @__PURE__ */ jsxs(
                            "tr",
                            {
                              className: ri % 2 === 0 ? "bg-white" : "bg-slate-50",
                              children:                       raciColIdx.map((c, colIdx) =>
                                /* @__PURE__ */ jsx(
                                  "td",
                                  {
                                    className: "px-3 py-2 text-slate-600 font-semibold",
                                    ...ax(annotateAnchors, `cell:raci:${ri}:${colIdx}`),
                                    children: contractExhibitEditable
                                      ? /* @__PURE__ */ jsx("input", {
                                          value: row[c] ?? "",
                                          onChange: (e) => updateRaciCell(ri, c, e.target.value),
                                          className:
                                            "w-full min-w-[64px] max-w-[140px] bg-blue-50 border border-blue-100 rounded px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-blue-300",
                                        })
                                      : c === 1
                                        ? row[1] || row[0] || ""
                                        : row[c] || "",
                                  },
                                  c
                                )
                              ),
                            },
                            ri
                          )
                        ),
                      }),
                    ],
                  }),
                }),
              }),
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs col-span-2", children: "Effort Estimate" }),
              /* @__PURE__ */ jsx("div", {
                className: "col-span-2",
                children: /* @__PURE__ */ jsx("div", {
                  className: "overflow-x-auto rounded-xl border border-slate-200",
                  children: /* @__PURE__ */ jsxs("table", {
                    className: "w-full text-left text-xs",
                    children: [
                      /* @__PURE__ */ jsx("thead", {
                        children: /* @__PURE__ */ jsx("tr", {
                          className: "bg-slate-900 text-[9px] font-black uppercase tracking-widest text-blue-300",
                          children: (resourceTable.header || []).map((h, hi) => /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: h }, hi)),
                        }),
                      }),
                      /* @__PURE__ */ jsx("tbody", {
                        className: "divide-y divide-slate-100",
                        children: (resourceTable.rows || []).map((row, ri) =>
                          /* @__PURE__ */ jsx(
                            "tr",
                            {
                              className: ri % 2 === 0 ? "bg-white" : "bg-slate-50",
                              children: row.map((cell, ci) =>
                                /* @__PURE__ */ jsx(
                                  "td",
                                  {
                                    className: "px-3 py-2 text-slate-600",
                                    ...ax(annotateAnchors, `cell:resources:${ri}:${ci}`),
                                    children: contractExhibitEditable
                                      ? /* @__PURE__ */ jsx("input", {
                                          value: cell ?? "",
                                          onChange: (e) => updateResourceCell(ri, ci, e.target.value),
                                          className:
                                            "w-full min-w-[48px] bg-blue-50 border border-blue-100 rounded px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-blue-300",
                                        })
                                      : cell || "",
                                  },
                                  ci
                                )
                              ),
                            },
                            ri
                          )
                        ),
                      }),
                    ],
                  }),
                }),
              }),
              /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Price Estimate" }),
              /* @__PURE__ */ jsxs("div", {
                ...ax(annotateAnchors, "exhibit.priceEstimate"),
                children: [
                  isContractEditMode
                    ? /* @__PURE__ */ jsx("textarea", {
                        value: contractSections.priceEstimateSummary,
                        onChange: (e) => setContractSections((p) => ({ ...p, priceEstimateSummary: e.target.value })),
                        className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-300 min-h-[100px]",
                        rows: 5,
                      })
                    : /* @__PURE__ */ jsx("span", { className: "text-slate-700 whitespace-pre-line", children: contractSections.priceEstimateSummary }),
                ],
              }),
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "bg-white border border-slate-200 rounded-xl p-5 mt-2",
            children: [
              /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1", children: "Disclaimer" }),
              /* @__PURE__ */ jsx("p", {
                className: "text-xs text-slate-500 leading-relaxed",
                children:
                  "The roles and resources identified above are based on tasks. However, any resource, adequately competent and experienced, may handle multiple responsibilities depending on the project plan and resource availability. Similarly, resources managing the project may be added or reduced depending on the project plan and resource availability.",
              }),
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "space-y-4 mt-6",
            ...ax(annotateAnchors, "section.paymentBlock"),
            children: [
              /* @__PURE__ */ jsx("h4", { className: "font-black text-sm text-slate-800 uppercase tracking-widest", children: "Payment Terms" }),
              /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-[180px_1fr] gap-y-3 gap-x-4 text-sm",
                children: [
                  /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Total Payment" }),
                  /* @__PURE__ */ jsxs("div", {
                    ...ax(annotateAnchors, "exhibit.totalPayment"),
                    children: [
                      isContractEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            value: contractSections.totalPayment,
                            onChange: (e) => setContractSections((p) => ({ ...p, totalPayment: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-300 w-48",
                          })
                        : /* @__PURE__ */ jsxs("span", {
                            className: "text-slate-700 font-bold",
                            children: String(contractSections.totalPayment ?? "").trim()
                              ? ["INR ", contractSections.totalPayment, "/- + applicable taxes"]
                              : "Not yet determined (one or more price lines have no rate)",
                          }),
                    ],
                  }),
                  /* @__PURE__ */ jsx("span", { className: "font-black text-slate-600 uppercase tracking-wide text-xs", children: "Payment Tranches" }),
                  /* @__PURE__ */ jsxs("div", {
                    ...ax(annotateAnchors, "exhibit.paymentTerms"),
                    children: [
                      isContractEditMode
                        ? /* @__PURE__ */ jsx("textarea", {
                            value: contractSections.paymentTerms,
                            onChange: (e) => setContractSections((p) => ({ ...p, paymentTerms: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-300 min-h-[80px]",
                            rows: 4,
                          })
                        : /* @__PURE__ */ jsx("span", { className: "text-slate-700 whitespace-pre-line", children: contractSections.paymentTerms }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
      (appendSections.length > 0 || appendSectionsEditable) &&
        /* @__PURE__ */ jsx(ContractAppendSections, {
          sections: appendSections,
          onChange: onAppendSectionsChange,
          editable: appendSectionsEditable,
          annotateAnchors,
          numberOffset: appendSectionNumberOffset,
        }),
      /* @__PURE__ */ jsx("div", {
        className: "border-t-2 border-slate-200 pt-4 mt-6",
        ...ax(annotateAnchors, "section.finalSignatures"),
        children: /* @__PURE__ */ jsxs("div", {
          className: "grid grid-cols-2 gap-10",
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "space-y-2",
              children: [
                /* @__PURE__ */ jsxs("p", { className: "text-xs font-bold text-slate-600", children: ["For ", contractSections.companyName] }),
                /* @__PURE__ */ jsx("div", {
                  className: "h-20 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-xs text-slate-400",
                  "data-testid": "signature-line",
                }),
              ],
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "space-y-2",
              children: [
                /* @__PURE__ */ jsxs("p", { className: "text-xs font-bold text-slate-600", children: ["For ", contractSections.advisorName] }),
                /* @__PURE__ */ jsx("div", {
                  className: "h-20 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-xs text-slate-400",
                  "data-testid": "signature-line",
                }),
              ],
            }),
          ],
        }),
      }),
    ],
  });
}
